/* eslint-disable max-lines-per-function */
/* eslint-disable functional/immutable-data */
"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import { useEffect, useState } from "react";

import { supabase } from "@/lib/supabase-client";
import { type AboutAttributes, type AboutContent } from "@/types/about-page";
import { drawSpiritAnalyzerLogo } from "@/utils/design-elements";
import { getSharePageUrl } from "@/utils/share-page-url";

interface RealTimeAboutProps {
  readonly initialData: AboutContent;
}

interface SupabasePayload {
  readonly [key: string]: unknown;
  readonly headline?: string;
  readonly lead_paragraph?: string;
  readonly body?: string;
  readonly spirit_analyzer?: string;
  readonly hero_image?: {
    readonly url: string;
    readonly width: number;
    readonly height: number;
    readonly alternativeText: string;
  };
  readonly seo?: {
    readonly description: string;
  };
}

const mapPayloadToContentAttributes = (payload: SupabasePayload): Partial<AboutAttributes> => {
  const mappedData: Partial<AboutAttributes> = {};

  if (payload.headline) mappedData.Headline = payload.headline;
  if (payload.lead_paragraph) mappedData.LeadParagraph = payload.lead_paragraph;
  if (payload.body) mappedData.Body = payload.body;
  if (payload.spirit_analyzer) mappedData.SpiritAnalyzer = payload.spirit_analyzer;

  return mappedData;
};

const updateContentFromPayload = (
  prevContent: AboutContent,
  newData: SupabasePayload,
): AboutContent => {
  const mappedData = mapPayloadToContentAttributes(newData);

  return {
    ...prevContent,
    data: {
      ...prevContent.data,
      attributes: {
        ...prevContent.data.attributes,
        ...mappedData,
      },
    },
  };
};

const SharePage = dynamic(() => import("@/components/social/share-page"), { ssr: false });

const RealTimeAboutClient = ({ initialData }: RealTimeAboutProps): React.JSX.Element => {
  const [content, setContent] = useState<AboutContent>(initialData);

  useEffect(() => {
    const channel = supabase
      .channel("abouts")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "abouts",
        },
        (payload: { eventType: string; new: SupabasePayload }) => {
          // console.log("Received payload:", payload);
          if (payload.eventType === "UPDATE" && payload.new) {
            setContent((prevContent) => updateContentFromPayload(prevContent, payload.new));
          }
        },
      )
      .subscribe();

    return (): void => {
      // eslint-disable-next-line no-console
      supabase.removeChannel(channel).catch((err) => console.warn("Failed to remove channel", err));
    };
  }, []);

  // console.log("RealTimeAbout content:", JSON.stringify(content, null, 2));

  const { Headline, LeadParagraph, Body, HeroImage, SpiritAnalyzer } = content.data.attributes;
  return (
    <div className="application-shell-cell application-shell-cell-body align-left">
      <main className="single-type">
        <div className="single-type-cell single-type-cell-content">
          <div className="level-1-heading-container-with-sharing-options">
            <div className="cell">
              <h1>{Headline}</h1>
            </div>
            <div className="cell sharing-options">
              <div className="rss-feed-with-share-page">
                <div className="rss-feed-with-share-page-cell">
                  <SharePage text={Headline} url={getSharePageUrl()} />
                </div>
              </div>
            </div>
          </div>
          <p className="lead-paragraph">{LeadParagraph}</p>
          <hr />
          {Body && <div dangerouslySetInnerHTML={{ __html: Body }} />}
          <hr />
          <div className="spirit-analyzer-logo-container">
            {drawSpiritAnalyzerLogo({
              height: 61,
              width: 320,
            })}
          </div>
          {SpiritAnalyzer && <div dangerouslySetInnerHTML={{ __html: SpiritAnalyzer }} />}
        </div>
        {HeroImage && HeroImage.data && HeroImage.data.attributes && (
          <div className="single-type-cell single-type-cell-hero-media">
            <div className="hero-media-container">
              <Image
                alt={HeroImage.data.attributes.alternativeText || ""}
                data-shape="antigen"
                height={HeroImage.data.attributes.height}
                priority={true}
                src={HeroImage.data.attributes.url}
                width={HeroImage.data.attributes.width}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default RealTimeAboutClient;
